import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * Listens for the `fias:credits-exhausted` window event and navigates the
 * user to the credits store via react-router (preserving SPA state).
 *
 * The event is dispatched from two non-React contexts:
 *   - `client/src/lib/api.ts` global request interceptor when the API
 *     returns 402 INSUFFICIENT_CREDITS with allBucketsExhausted=true.
 *   - `client/src/lib/hooks/useStreaming.ts` WebSocket onError handler
 *     when a streaming AI call errors with INSUFFICIENT_CREDITS.
 *
 * Centralizing the navigation here means there's exactly one place that
 * decides whether to navigate, the navigation is router-aware (no full
 * page reload), and we never trigger a redirect loop while already on
 * the credits page.
 *
 * Mount this component once, inside the router context, near the top of
 * the app tree.
 */
export function CreditExhaustionListener() {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handler = () => {
      // Avoid redirect loops while the user is already on the credits page.
      if (location.pathname.startsWith('/credits')) {
        return;
      }
      navigate('/credits?reason=out-of-credits');
    };

    window.addEventListener('fias:credits-exhausted', handler);
    return () => {
      window.removeEventListener('fias:credits-exhausted', handler);
    };
  }, [navigate, location.pathname]);

  return null;
}
